import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { GhostCta, PrimaryCta, Section, useLanding } from './LandingKit';
import { fluid, isWeb, prefersReducedMotion } from './landingTokens';
import { PIXEL_ART, PixelAvatar } from './LandingPixels';
import LandingBotOutput from './LandingBotOutput';

const BOTS = [
  {
    name: 'Video creator', specialty: 'Stories made for the screen.', icon: 'film', avatar: PIXEL_ART.creative, kind: 'video',
    instructions: 'Cinematic scenes. Natural pacing. Music that fits the moment.',
    prompt: 'Create a 30-second film about a summer by the sea, with an inspiring voiceover and a cinematic soundtrack.',
    file: 'golden-hour.mp4', format: 'Video · MP4',
    note: 'Scenes, voiceover, and music. Brought together.',
  },
  {
    name: 'Game developer', specialty: 'Ideas you can play.', icon: 'gamepad', avatar: PIXEL_ART.game, kind: 'game',
    instructions: 'Easy to pick up. Fun to replay. Always include clear controls and scoring.',
    prompt: 'Build a retro space game where I dodge asteroids and collect stars. Make it work on my phone, too.',
    file: 'star-runner.html', format: 'Game · HTML',
    note: 'Touch controls, scoring, and one more try.',
  },
  {
    name: 'Web developer', specialty: 'Your own corner of the web.', icon: 'code', avatar: PIXEL_ART.developer, kind: 'website',
    instructions: 'Clean layouts. Readable type. A great experience on every screen.',
    prompt: 'Build a website for my hiking club, with upcoming adventures, a little about us, and a way to get in touch.',
    file: 'weekend-trail-club.html', format: 'Website · HTML & CSS',
    note: 'A responsive website, with source you can edit.',
  },
  {
    name: 'Creative writer', specialty: 'A little more imagination.', icon: 'pen', avatar: PIXEL_ART.writer, kind: 'writing',
    instructions: 'Vivid characters. Playful language. An ending that leaves a little hope.',
    prompt: 'Write the opening of a bedtime story about a small fox who collects fallen stars.',
    file: 'the-fox-and-the-stars.docx', format: 'Story · Word',
    note: 'A first draft, ready for your next idea.',
  },
];

const BENEFITS = [
  { number: '01', title: 'Make it personal.', copy: 'Set their role, style, and instructions. Make each AI employee your own.' },
  { number: '02', title: 'Pick up where you left off.', copy: 'Keep your team in the sidebar, ready for the next assignment.' },
  { number: '03', title: 'Make room for more.', copy: 'Hire a community bot or create one for the work you have in mind.' },
];

function RoleChoice({ bot, active, onPress, compact, colors, reducedMotion }) {
  const [focused, setFocused] = useState(false);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={'Preview ' + bot.name}
      {...(isWeb ? { 'aria-pressed': active, 'aria-controls': 'landing-team-preview', dataSet: { landingBotChoice: 'true' } } : { accessibilityState: { selected: active } })}
      onPress={onPress}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={({ hovered, pressed }) => [
        styles.choice,
        {
          flexBasis: compact ? '47%' : 0,
          paddingHorizontal: compact ? 10 : 20,
          paddingVertical: compact ? 12 : 18,
          borderColor: focused ? colors.ink : active ? colors.selectedRule : 'transparent',
          backgroundColor: active ? colors.card : hovered ? colors.hover : 'transparent',
          opacity: pressed ? 0.76 : 1,
          ...(isWeb ? {
            outlineStyle: 'none',
            boxShadow: active ? colors.shadow : 'none',
            transition: reducedMotion ? 'none' : 'background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
          } : {}),
        },
      ]}
    >
      <PixelAvatar source={bot.avatar} size={compact ? 40 : 64} />
      <View style={{ flex: 1, minWidth: 0, gap: 5 }}>
        <Text weight="semibold" style={{ color: active ? colors.ink : colors.muted, fontSize: compact ? 13 : 15, lineHeight: 20 }}>{bot.name}</Text>
        {!compact && <Text style={{ color: colors.muted, fontSize: 11, lineHeight: 16 }}>{bot.specialty}</Text>}
      </View>
    </Pressable>
  );
}

export default function LandingBots() {
  const { c, width, isDark } = useLanding();
  const compact = width < 850;
  const narrow = width < 500;
  const [selected, setSelected] = useState(0);
  const bot = BOTS[selected];
  const reducedMotion = prefersReducedMotion();
  const headingSize = fluid(width, 40, 66);
  const colors = {
    ink: c.ink,
    muted: isDark ? '#A4AAA7' : '#646A66',
    background: isDark ? '#141715' : '#F5F6F3',
    card: isDark ? '#1D211F' : '#FFFFFF',
    inset: isDark ? '#181C19' : '#F7F8F5',
    hover: isDark ? '#202522' : '#EDEFEA',
    rule: isDark ? '#323834' : '#E4E7E0',
    selectedRule: isDark ? '#67716A' : '#B9C1B8',
    shadow: isDark ? '0 3px 12px rgba(0,0,0,.14)' : '0 3px 12px rgba(24,34,26,.05)',
  };

  return (
    <Section label="Meet Gab AI Bots" background={colors.background} contentStyle={{ paddingTop: fluid(width, 64, 96), paddingBottom: fluid(width, 64, 88) }}>
      <View style={{ alignItems: 'center', maxWidth: 760, alignSelf: 'center' }}>
        <Text weight="medium" style={{ color: colors.muted, fontSize: 15, marginBottom: 18 }}>Gab AI Bots</Text>
        <Text tag="h2" weight="semibold" style={{ color: colors.ink, fontSize: headingSize, lineHeight: Math.round(headingSize * 1.08), letterSpacing: -headingSize * 0.045, textAlign: 'center' }}>
          {'Build your AI Team.'}
        </Text>
        <Text tag="p" style={{ color: colors.muted, fontSize: fluid(width, 17, 20), lineHeight: fluid(width, 27, 31), textAlign: 'center', maxWidth: 620, marginTop: 22, ...(isWeb ? { textWrap: 'balance' } : {}) }}>
          AI employees, with your way of doing things. Give them a role and a little direction. Come back to work ready for your review.
        </Text>
      </View>

      <View style={{ marginTop: fluid(width, 32, 44) }}>
        <Text style={{ color: colors.muted, fontSize: 12, textAlign: 'center', marginBottom: 14 }}>Choose a role. See the possibilities.</Text>
        <View accessibilityRole={isWeb ? 'group' : undefined} accessibilityLabel="Choose a bot to preview" style={styles.choices}>
          {BOTS.map((item, index) => (
            <RoleChoice key={item.name} bot={item} active={selected === index} onPress={() => setSelected(index)} compact={width < 1100} colors={colors} reducedMotion={reducedMotion} />
          ))}
        </View>
      </View>

      <View nativeID="landing-team-preview" accessibilityLabel={bot.name + ' example'} style={[styles.stage, { backgroundColor: colors.card, borderColor: colors.rule, marginTop: 20, ...(isWeb ? { boxShadow: colors.shadow } : {}) }]}>
        <View style={[styles.toolbar, { borderBottomColor: colors.rule, paddingHorizontal: narrow ? 20 : 32 }]}>
          <View style={styles.row}>
            <Icon name={bot.icon} size={14} color={colors.muted} />
            <Text weight="medium" style={{ color: colors.ink, fontSize: 13 }}>{bot.name}</Text>
          </View>
          <Text style={{ color: colors.muted, fontSize: 11 }}>Illustrative example</Text>
        </View>
        <View style={{ flexDirection: compact ? 'column' : 'row' }}>
          <View style={{ flex: compact ? undefined : 0.82, minWidth: 0, padding: narrow ? 22 : 32, paddingVertical: narrow ? 24 : 36, gap: 24 }}>
            <View>
              <Text weight="medium" style={{ color: colors.muted, fontSize: 12, marginBottom: 10 }}>The way you like it</Text>
              <Text style={{ color: colors.ink, fontSize: 15, lineHeight: 25 }}>{bot.instructions}</Text>
            </View>
            <View style={{ paddingTop: 24, borderTopWidth: 1, borderColor: colors.rule }}>
              <Text weight="medium" style={{ color: colors.muted, fontSize: 12, marginBottom: 12 }}>Your next assignment</Text>
              <Text style={{ color: colors.ink, fontSize: fluid(width, 17, 19), lineHeight: 29, letterSpacing: -0.25 }}>{'“' + bot.prompt + '”'}</Text>
            </View>
            <View style={{ ...styles.row, marginTop: compact ? 0 : 'auto', paddingTop: compact ? 0 : 12 }}>
              <View style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: colors.inset, alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="check" size={11} color={colors.muted} />
              </View>
              <Text style={{ color: colors.muted, fontSize: 12 }}>Your direction. Their next project.</Text>
            </View>
          </View>
          <View style={{ flex: compact ? undefined : 1.18, minWidth: 0, padding: narrow ? 16 : 24, backgroundColor: colors.inset, borderLeftWidth: compact ? 0 : 1, borderTopWidth: compact ? 1 : 0, borderColor: colors.rule }}>
            <View style={{ ...styles.row, justifyContent: 'space-between', marginBottom: 16, paddingHorizontal: 4 }}>
              <Text weight="medium" style={{ color: colors.muted, fontSize: 12 }}>Made for your next step.</Text>
              <Text style={{ color: colors.muted, fontSize: 11 }}>{bot.format}</Text>
            </View>
            <View key={bot.kind} style={{ borderRadius: 16, overflow: 'hidden', backgroundColor: colors.card }}>
              <LandingBotOutput kind={bot.kind} compact={narrow} />
              <View style={{ ...styles.row, padding: narrow ? 16 : 18, borderTopWidth: 1, borderTopColor: colors.rule }}>
                <Icon name={bot.icon} size={15} color={colors.muted} />
                <Text weight="medium" style={{ color: colors.ink, fontSize: 12, flex: 1, minWidth: 0 }}>{bot.file}</Text>
                <Icon name="check" size={12} color={colors.muted} />
              </View>
            </View>
            <Text style={{ color: colors.muted, fontSize: 12, lineHeight: 20, marginTop: 14, paddingHorizontal: 4 }}>{bot.note}</Text>
          </View>
        </View>
      </View>
      <Text {...(isWeb ? { 'aria-live': 'polite', 'aria-atomic': true } : { accessibilityLiveRegion: 'polite' })} style={styles.announcement}>
        {bot.name + ' example selected. ' + bot.file}
      </Text>

      <View style={{ flexDirection: compact ? 'column' : 'row', gap: compact ? 24 : 40, marginTop: fluid(width, 32, 40) }}>
        {BENEFITS.map(item => (
          <View key={item.number} style={{ flex: compact ? undefined : 1, minWidth: 0, flexDirection: 'row', gap: 14 }}>
            <Text aria-hidden={isWeb ? true : undefined} weight="medium" style={{ color: colors.muted, fontSize: 12, paddingTop: 4 }}>{item.number}</Text>
            <View style={{ flex: 1 }}>
              <Text tag="h3" weight="semibold" style={{ color: colors.ink, fontSize: 17, lineHeight: 24, letterSpacing: -0.3 }}>{item.title}</Text>
              <Text style={{ color: colors.muted, fontSize: 14, lineHeight: 23, marginTop: 8 }}>{item.copy}</Text>
            </View>
          </View>
        ))}
      </View>
      <View style={{ alignItems: 'center', marginTop: fluid(width, 32, 44), gap: narrow ? 4 : 22, flexDirection: narrow ? 'column' : 'row', justifyContent: 'center' }}>
        <View><PrimaryCta tone="invert" label="Hire your first AI employee" href="/agents" /></View>
        <View><GhostCta label="How bots work" href="/docs/agents" /></View>
      </View>
    </Section>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  choices: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  choice: { flexGrow: 1, borderRadius: 18, borderWidth: 1, flexDirection: 'row', alignItems: 'center', gap: 10, minWidth: 0, ...(isWeb ? { cursor: 'pointer' } : {}) },
  stage: { borderRadius: 24, borderWidth: 1, overflow: 'hidden' },
  toolbar: { minHeight: 58, borderBottomWidth: 1, paddingVertical: 16, flexDirection: 'row', gap: 12, alignItems: 'center', justifyContent: 'space-between' },
  announcement: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', ...(isWeb ? { clipPath: 'inset(50%)', whiteSpace: 'nowrap' } : {}) },
});
